import MerchantPayoutAdminPanel from "./components/MerchantPayoutAdminPanel";
import MerchantMediaAdminPanel from "./components/MerchantMediaAdminPanel";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReactLoading from "react-loading";
import { ToastContainer, toast, darkToast } from "../../Components/Toast";
import { serverUrl } from "../../Utils/server";
import "./Merchants.css";


const operationalDayLabels = [
  ["monday", "Lunes"],
  ["tuesday", "Martes"],
  ["wednesday", "Miércoles"],
  ["thursday", "Jueves"],
  ["friday", "Viernes"],
  ["saturday", "Sábado"],
  ["sunday", "Domingo"],
];

const defaultOperationalHours = {
  monday: { enabled: true, open: "08:00", close: "18:00" },
  tuesday: { enabled: true, open: "08:00", close: "18:00" },
  wednesday: { enabled: true, open: "08:00", close: "18:00" },
  thursday: { enabled: true, open: "08:00", close: "18:00" },
  friday: { enabled: true, open: "08:00", close: "18:00" },
  saturday: { enabled: true, open: "08:00", close: "18:00" },
  sunday: { enabled: false, open: "08:00", close: "18:00" },
};

const availabilityDefaultMessages = {
  open: "Abierto",
  paused: "Cerrado",
  saturated: "Saturado",
  temporarily_closed: "Cerrado",
};

function getDefaultAvailabilityMessage(status) {
  return availabilityDefaultMessages[status] || availabilityDefaultMessages.open;
}


function normalizeAvailabilityHours(hours = {}) {
  return operationalDayLabels.reduce((acc, [dayKey]) => {
    const current = hours?.[dayKey] || {};
    const fallback = defaultOperationalHours[dayKey];

    acc[dayKey] = {
      enabled: current.enabled !== undefined ? current.enabled === true : fallback.enabled,
      open: current.open || fallback.open,
      close: current.close || fallback.close,
    };

    return acc;
  }, {});
}


const emptyForm = {
  name: "",
  legalName: "",
  email: "",
  phone: "",
  phoneCountryCode: "",
  phoneLocalNumber: "",
  address: "",
  reference: "",
  latitude: "",
  longitude: "",
  mapUrl: "",
  logoUrl: "",
  coverImageUrl: "",
  imageAlt: "",
  brandingStatus: "pending",
  status: "pending",
  planKey: "free",
  isActive: true,
  slaEnabled: true,
  slaMerchantLimitMinutes: "25",
  slaWarningAfterMinutes: "20",
  slaCourierReserveMinutes: "10",
  slaPreparationMode: "manual",
  slaAutoStartPreparationAfterAccept: false,
  slaAutoStartPreparationDelaySeconds: "10",
  slaAllowReadyForPickupFromAccepted: false,
  availabilityEnabled: true,
  availabilityManualStatus: "open",
  availabilityMessage: "",
  availabilityTimezone: "America/El_Salvador",
  availabilityHours: defaultOperationalHours,
  financeEnabled: true,
  financeServiceType: "delivery_food",
  financeCommissionPercent: "14",
  financeFixedCommissionAmount: "0",
  financePenaltyEnabled: true,
  financePenaltyAfterAcceptedMinutes: "10",
  financePenaltyPercent: "50",
  financePenaltyFixedAmount: "0",
  paymentProfileLegalName: "",
  paymentProfileCommercialName: "",
  paymentProfileDocumentType: "",
  paymentProfileDocumentNumber: "",
  paymentProfileNrc: "",
  paymentProfileEmail: "",
  paymentProfilePhone: "",
  paymentProfileLegalRepresentativeName: "",
  paymentProfileLegalRepresentativeDocument: "",
  paymentProfileBankName: "",
  paymentProfileBankAccountMasked: "",
  paymentProfileBankAccountHolder: "",
};

const statusLabels = {
  pending: "Pendiente",
  active: "Activo",
  suspended: "Suspendido",
  deleted: "Eliminado",
};

const planLabels = {
  free: "Free",
  pro: "Pro",
  premium: "Premium",
  custom: "Custom",
};

function normalizeText(value) {
  return String(value || "").toLowerCase().trim();
}

function onlyDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function formatDate(value) {
  if (!value) return "-";
  return String(value).split("T")[0];
}

function toNullableNumber(value) {
  const clean = String(value || "").trim();
  if (!clean) return null;
  const parsed = Number(clean);
  return Number.isFinite(parsed) ? parsed : null;
}

function toPositiveIntegerForm(value, fallback) {
  const parsed = Number(String(value || "").trim());
  if (!Number.isFinite(parsed)) return fallback;
  return Math.round(parsed);
}

function toPositiveNumberForm(value, fallback) {
  const parsed = Number(String(value || "").trim());
  if (!Number.isFinite(parsed)) return fallback;
  return parsed;
}


function getPayoutProofUrl(row = {}) {
  return String(row.proofUrl || row.proofFileUrl || row.paymentProofUrl || "").trim();
}

function PayoutProofAction({ row }) {
  const proofUrl = getPayoutProofUrl(row);

  if (!proofUrl) {
    return <strong>—</strong>;
  }

  return (
    <a
      // PAGOS.REAL.2F_MERCHANTS_PROOF_ACTION
      href={proofUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="merchant-proof-file-action"
      title="Abrir comprobante de pago"
    >
      Ver comprobante
    </a>
  );
}

function formatMoney(value) {
  const parsed = Number(value || 0);
  return `$${parsed.toFixed(2)}`;
}

function getFinanceAlertCount(financeSummary) {
  if (!financeSummary) return 0;
  const payoutCount = Array.isArray(financeSummary.payoutRequests)
    ? financeSummary.payoutRequests.filter((item) => ["requested", "under_review", "approved"].includes(item.status)).length
    : 0;
  const incidentCount = Number(financeSummary.openIncidents || 0);
  return payoutCount + incidentCount;
}


// QA6.60E.2C.2C - Admin payouts semaforo, actions and traceability helpers
function getPayoutSemaforo(row = {}) {
  if (row.holdReason || row.disputeReason) return "alerta";

  const deadline = row.slaDeadlineAt ? new Date(row.slaDeadlineAt).getTime() : 0;
  const isClosed = ["paid", "received_confirmed"].includes(row.status);
  if (deadline && deadline < Date.now() && !isClosed) return "alerta";

  if (row.status === "requested") return "rojo";
  if (["under_review", "approved"].includes(row.status)) return "amarillo";
  if (row.status === "paid") return "verde";
  if (row.status === "received_confirmed") return "cerrado";
  return "alerta";
}

function getPayoutSemaforoLabel(row = {}) {
  const semaforo = getPayoutSemaforo(row);
  if (semaforo === "rojo") return "Pendiente Admin";
  if (semaforo === "amarillo") return "En proceso";
  if (semaforo === "verde") return "Pagado";
  if (semaforo === "cerrado") return "Cerrado";
  return "Requiere atención";
}

function getPayoutAdminAction(row = {}) {
  if (row.status === "requested") return "under_review";
  if (row.status === "under_review") return "approved";
  if (row.status === "approved") return "paid";
  return "";
}

function getPayoutActionLabel(status) {
  const labels = {
    under_review: "Recibir / revisar",
    approved: "Aprobar pago",
    paid: "Registrar pago",
    rejected: "Rechazar",
    cancelled: "Cancelar",
    received_confirmed: "Marcar recibido"
  };
  return labels[status] || "Actualizar";
}

function getPayoutMethodLabel(method) {
  const labels = {
    bank_transfer: "Transferencia bancaria",
    cash: "Efectivo",
    wallet: "Wallet",
    other: "Otro",
    "": "No definido"
  };
  return labels[method || ""] || method;
}

function getPayoutAuditLabel(eventType) {
  const labels = {
    requested: "Solicitud creada",
    status_under_review: "Recibida por Admin",
    status_approved: "Aprobada",
    status_paid: "Pago registrado",
    status_received_confirmed: "Comercio confirmó recibido",
    status_rejected: "Rechazada",
    status_cancelled: "Cancelada",
    status_hold: "Retenida / incidencia"
  };
  return labels[eventType] || eventType;
}


// QA6.60E.2C.4 - Admin payout real vs QA clarity helpers
function isQaTracePayout(row = {}) {
  return String(row.requestNumber || "").startsWith("QA-TRACE-");
}

function getSortedPayoutRequests(financeSummary) {
  const rows = Array.isArray(financeSummary?.payoutRequests) ? financeSummary.payoutRequests : [];

  return [...rows].sort((a, b) => {
    const aQa = isQaTracePayout(a);
    const bQa = isQaTracePayout(b);

    if (aQa !== bQa) return aQa ? 1 : -1;

    const aDate = new Date(a.requestedAt || a.createdAt || 0).getTime();
    const bDate = new Date(b.requestedAt || b.createdAt || 0).getTime();

    return bDate - aDate;
  });
}

function getRealPendingPayoutBalance(financeSummary) {
  const rows = Array.isArray(financeSummary?.payoutRequests) ? financeSummary.payoutRequests : [];
  const pendingStatuses = ["requested", "under_review", "approved"];

  const total = rows
    .filter((row) => !isQaTracePayout(row) && pendingStatuses.includes(row.status))
    .reduce((sum, row) => sum + Number(row.netAmountRequested || 0), 0);

  return Math.round(total * 100) / 100;
}

function getQaTracePayoutCount(financeSummary) {
  const rows = Array.isArray(financeSummary?.payoutRequests) ? financeSummary.payoutRequests : [];
  return rows.filter((row) => isQaTracePayout(row)).length;
}
// QA6.60E.2C.4 END

function formatPayoutDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("es-SV", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}
// QA6.60E.2C.2C END

function getLedgerTypeLabel(type) {
  const labels = {
    order_sale: "Venta liquidable",
    penalty: "Penalización SLA",
    adjustment: "Ajuste manual",
    payout_hold: "Retenido para pago",
    payout_release: "Liberado",
  };
  return labels[type] || type || "-";
}

function getLedgerStatusLabel(status) {
  const labels = {
    available: "Disponible",
    requested: "Solicitado",
    paid: "Pagado",
    cancelled: "Cancelado",
    reversed: "Reversado",
  };
  return labels[status] || status || "-";
}

function getPayoutStatusLabel(status) {
  const labels = {
    requested: "Solicitado",
    under_review: "En revisión",
    approved: "Aprobado",
    paid: "Pagado",
    rejected: "Rechazado",
    cancelled: "Cancelado",
    received_confirmed: "Recibido confirmado",
  };
  return labels[status] || status || "-";
}

function getBadgeClass(status) {
  if (status === "active") return "merchant-badge merchant-badge-active";
  if (status === "suspended") return "merchant-badge merchant-badge-suspended";
  if (status === "deleted") return "merchant-badge merchant-badge-deleted";
  return "merchant-badge merchant-badge-pending";
}

function getPlanClass(planKey) {
  if (planKey === "premium") return "merchant-plan merchant-plan-premium";
  if (planKey === "pro") return "merchant-plan merchant-plan-pro";
  if (planKey === "custom") return "merchant-plan merchant-plan-custom";
  return "merchant-plan merchant-plan-free";
}

function getMerchantInitials(name) {
  const clean = String(name || "C").trim();
  const parts = clean.split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((part) => part[0]).join("").toUpperCase() || "C";
}

function getPhoneDisplay(merchant) {
  if (merchant?.phone) return merchant.phone;
  if (merchant?.phoneCountryCode && merchant.phoneLocalNumber) {
    return `${merchant.phoneCountryCode}${merchant.phoneLocalNumber}`;
  }
  return "-";
}

function getMerchantId(merchant) {
  return String(merchant?.id || merchant?._id || "");
}

function slugifyMerchantAccess(value) {
  const base = String(value || "comercio")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return base || "comercio";
}

function getDefaultMerchantAccessUser(merchant) {
  const base = slugifyMerchantAccess(merchant?.name || merchant?.legalName || "comercio");
  return `${base}@comercios.superagrodig.com`;
}

function valueToString(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

function buildAuthHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `JWT ${localStorage.getItem("token")}`,
  };
}

function Merchants() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingSla, setSavingSla] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [financeSummary, setFinanceSummary] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeError, setFinanceError] = useState("");
  const [payoutActionBusy, setPayoutActionBusy] = useState("");
  const [payoutDetail, setPayoutDetail] = useState(null);

  const getAdminAuthConfig = () => {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("adminToken") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("x-auth-token") ||
      "";

    return {
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
            "x-auth-token": token
          }
        : {}
    };
  };

  const uploadPayoutProofFile = async (row, file) => {
    // PAGOS.REAL.2E_UPLOAD_PROOF_FILE_HANDLER
    if (!row?.id) {
      throw new Error("La solicitud de pago no tiene identificador válido.");
    }

    if (!file) {
      throw new Error("Debes seleccionar un comprobante PDF o imagen.");
    }

    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

    if (!allowed.includes(file.type)) {
      throw new Error("Formato no permitido. Sube PDF, JPG, PNG o WEBP.");
    }

    const maxBytes = 12 * 1024 * 1024;

    if (file.size > maxBytes) {
      throw new Error("Archivo demasiado grande. Máximo 12 MB.");
    }

    const authConfig = getAdminAuthConfig();

    const response = await axios.post(
      `${serverUrl}/merchant-finance/admin/payout-requests/${row.id}/proof-file`,
      file,
      {
        headers: {
          ...(authConfig.headers || {}),
          "Content-Type": file.type,
          "X-File-Name": file.name || "comprobante"
        }
      }
    );

    return response.data || {};
  };

  const loadMerchantPaymentRecipientProfile = async (merchantId) => {
    // PAGOS.REAL.2J_PAYMENT_PROFILE_API
    if (!merchantId) {
      throw new Error("Selecciona un comercio para cargar su ficha legal/bancaria.");
    }

    const response = await axios.get(
      `${serverUrl}/merchant-finance/admin/merchant/${merchantId}/payment-recipient-profile`,
      getAdminAuthConfig()
    );

    return response.data?.profile || {};
  };

  const saveMerchantPaymentRecipientProfile = async (merchantId, profile) => {
    if (!merchantId) {
      throw new Error("Selecciona un comercio para guardar su ficha legal/bancaria.");
    }

    const response = await axios.patch(
      `${serverUrl}/merchant-finance/admin/merchant/${merchantId}/payment-recipient-profile`,
      profile,
      getAdminAuthConfig()
    );

    return response.data?.profile || {};
  };

  function applyPaymentRecipientProfileToForm(profile = {}) {
    // PAGOS.REAL.2J1_PROFILE_OUTSIDE_PAYMENT_FUNCTIONS
    setForm((current) => ({
      ...current,
      paymentProfileLegalName: profile.legalName || "",
      paymentProfileCommercialName: profile.commercialName || "",
      paymentProfileDocumentType: profile.documentType || "",
      paymentProfileDocumentNumber: profile.documentNumber || "",
      paymentProfileNrc: profile.nrc || "",
      paymentProfileEmail: profile.email || "",
      paymentProfilePhone: profile.phone || "",
      paymentProfileLegalRepresentativeName: profile.legalRepresentativeName || "",
      paymentProfileLegalRepresentativeDocument: profile.legalRepresentativeDocument || "",
      paymentProfileBankName: profile.bankName || "",
      paymentProfileBankAccountMasked: profile.bankAccountMasked || "",
      paymentProfileBankAccountHolder: profile.bankAccountHolder || profile.legalName || "",
    }));
  }

  const loadPaymentRecipientProfileIntoForm = async (merchantId) => {
    if (!merchantId) return;

    try {
      const profile = await loadMerchantPaymentRecipientProfile(merchantId);
      applyPaymentRecipientProfileToForm(profile || {});
    } catch (error) {
      console.error("[PAYMENT_RECIPIENT_PROFILE_LOAD] ERROR:", error);
    }
  };

  const savePaymentRecipientProfileFromForm = async () => {
    if (!selectedMerchantId) {
      toast("Selecciona un comercio antes de guardar la ficha legal/bancaria", darkToast);
      return;
    }

    const payload = {
      legalName: form.paymentProfileLegalName || form.legalName || form.name || "",
      commercialName: form.paymentProfileCommercialName || form.name || "",
      documentType: form.paymentProfileDocumentType || "",
      documentNumber: form.paymentProfileDocumentNumber || "",
      nrc: form.paymentProfileNrc || "",
      email: form.paymentProfileEmail || form.email || "",
      phone: form.paymentProfilePhone || form.phone || "",
      legalRepresentativeName: form.paymentProfileLegalRepresentativeName || "",
      legalRepresentativeDocument: form.paymentProfileLegalRepresentativeDocument || "",
      bankName: form.paymentProfileBankName || "",
      bankAccountMasked: form.paymentProfileBankAccountMasked || "",
      bankAccountHolder: form.paymentProfileBankAccountHolder || form.paymentProfileLegalName || form.legalName || form.name || "",
    };

    if (!payload.bankName || !payload.bankAccountMasked || !payload.bankAccountHolder) {
      toast("Completa banco, cuenta destino y titular de cuenta en la ficha del comercio", darkToast);
      return;
    }

    if (!payload.documentNumber || !payload.email || !payload.phone) {
      toast("Completa documento fiscal/DUI/NIT, correo y teléfono en la ficha del comercio", darkToast);
      return;
    }

    setSavingPaymentRecipientProfile(true);

    try {
      const saved = await saveMerchantPaymentRecipientProfile(selectedMerchantId, payload);
      applyPaymentRecipientProfileToForm(saved || {});
      toast("Ficha legal/bancaria del comercio guardada correctamente", darkToast);
    } catch (error) {
      const message =
        error?.response?.data?.msg ||
        error?.response?.data ||
        error?.message ||
        "No se pudo guardar la ficha legal/bancaria del comercio";
      toast(String(message), darkToast);
    } finally {
      setSavingPaymentRecipientProfile(false);
    }
  };

  const updatePayoutStatus = async (row, targetStatus, casefilePayloadExtra = {}) => {
    if (!row?.id || !targetStatus) return;

    const actionLabel = getPayoutActionLabel(targetStatus);
    const confirmMessage = `Confirmar acción: ${actionLabel}\nSolicitud: ${row.requestNumber}\nMonto: ${formatMoney(row.netAmountRequested || 0)}`;

    // PAGOS.REAL.2D2_AXIOS_FULL_PAYLOAD_HANDLER
    const hasCasefilePayload =
      casefilePayloadExtra &&
      typeof casefilePayloadExtra === "object" &&
      !Array.isArray(casefilePayloadExtra);

    if (!hasCasefilePayload && !window.confirm(confirmMessage)) return;

    const payload = hasCasefilePayload
      ? { ...casefilePayloadExtra, status: targetStatus }
      : {
          status: targetStatus,
          comment: ""
        };

    if (!hasCasefilePayload && targetStatus === "paid") {
      const transferReference = window.prompt("Referencia de transferencia o comprobante:", row.transferReference || "");
      if (!transferReference) {
        window.alert("Para registrar como pagado se requiere referencia.");
        return;
      }

      const proofUrl = window.prompt("URL o referencia del comprobante/evidencia:", row.proofUrl || "");
      const bankName = window.prompt("Banco o método de pago:", row.bankName || "Banco");
      const bankAccountMasked = window.prompt("Cuenta destino enmascarada, si aplica:", row.bankAccountMasked || "");

      payload.paymentMethod = "bank_transfer";
      payload.transferReference = transferReference;
      payload.proofUrl = proofUrl || "";
      payload.bankName = bankName || "";
      payload.bankAccountMasked = bankAccountMasked || "";
      payload.comment = "Pago registrado desde Admin.";
    } else if (!hasCasefilePayload) {
      const comment = window.prompt(`Comentario para ${actionLabel}:`, "");
      payload.comment = comment || "";
    }

    setPayoutActionBusy(row.id);

    try {
      const response = await axios.patch(
        `${serverUrl}/merchant-finance/admin/payout-requests/${row.id}/status`,
        payload,
        getAdminAuthConfig()
      );

      const updated = response.data || {};

      setFinanceSummary((previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          payoutRequests: (previous.payoutRequests || []).map((item) =>
            item.id === row.id ? { ...item, ...updated } : item
          )
        };
      });

      if (payoutDetail && payoutDetail.id === row.id) {
        setPayoutDetail({ ...payoutDetail, ...updated });
      }
    } catch (error) {
      console.error("[ADMIN_PAYOUT_STATUS_UPDATE] ERROR:", error);
      const message =
        error?.response?.data?.msg ||
        error?.response?.data ||
        "No se pudo actualizar la solicitud de pago.";
      window.alert(message);
    } finally {
      setPayoutActionBusy("");
    }
  };

  const [savingFinanceConfig, setSavingFinanceConfig] = useState(false);
  const [savingPaymentRecipientProfile, setSavingPaymentRecipientProfile] = useState(false);
  const [activeMerchantTab, setActiveMerchantTab] = useState("perfil");
  const [merchants, setMerchants] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [countryOptions, setCountryOptions] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [countriesError, setCountriesError] = useState("");
  const [selectedMerchantId, setSelectedMerchantId] = useState("");
  const [locatingLocation, setLocatingLocation] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [credentialPanel, setCredentialPanel] = useState({
    open: false,
    loading: false,
    merchant: null,
    name: "",
    email: "",
    temporaryPassword: "",
    user: null,
    error: "",
    successMessage: "",
  });
  const [form, setForm] = useState(emptyForm);

  const stats = useMemo(() => {
    return {
      total: merchants.length,
      active: merchants.filter((item) => item.status === "active").length,
      pending: merchants.filter((item) => item.status === "pending").length,
      suspended: merchants.filter((item) => item.status === "suspended").length,
    };
  }, [merchants]);

  const getDefaultCountryCode = useCallback(
    (options = countryOptions) => {
      const preferred = options.find((option) => option.iso2 === "SV");
      return preferred?.code || options[0]?.code || "";
    },
    [countryOptions]
  );

  const resetForm = useCallback(() => {
    setSelectedMerchantId("");
    setForm({
      ...emptyForm,
      phoneCountryCode: getDefaultCountryCode(),
    });
  }, [getDefaultCountryCode]);

  const filteredMerchants = useMemo(() => {
    const text = normalizeText(search);

    return merchants.filter((merchant) => {
      const matchesSearch =
        !text ||
        normalizeText(merchant.name).includes(text) ||
        normalizeText(merchant.legalName).includes(text) ||
        normalizeText(merchant.email).includes(text) ||
        normalizeText(merchant.phone).includes(text) ||
        normalizeText(merchant.phoneCountryCode).includes(text) ||
        normalizeText(merchant.phoneLocalNumber).includes(text) ||
        normalizeText(merchant.imageAlt).includes(text);

      const matchesStatus = statusFilter === "all" || merchant.status === statusFilter;
      const matchesPlan = planFilter === "all" || merchant.planKey === planFilter;

      return matchesSearch && matchesStatus && matchesPlan;
    });
  }, [merchants, search, statusFilter, planFilter]);

  const loadMerchantFinanceSummary = useCallback((merchantId) => {
    if (!merchantId) {
      setFinanceSummary(null);
      setFinanceError("");
      return;
    }

    setFinanceLoading(true);
    setFinanceError("");

    axios
      .get(`${serverUrl}/merchant-finance/admin/merchant/${merchantId}/summary`, {
        headers: buildAuthHeaders(),
      })
      .then((response) => {
        const data = response.data || {};
        const config = data.financeConfig || {};

        setFinanceSummary(data);
        setForm((current) => ({
          ...current,
          financeEnabled: config.enabled !== false,
          financeServiceType: config.serviceType || "delivery_food",
          financeCommissionPercent: valueToString(config.commissionPercent ?? 14),
          financeFixedCommissionAmount: valueToString(config.fixedCommissionAmount ?? 0),
          financePenaltyEnabled: config.penaltyEnabled !== false,
          financePenaltyAfterAcceptedMinutes: valueToString(config.penaltyAfterAcceptedMinutes ?? 10),
          financePenaltyPercent: valueToString(config.penaltyPercent ?? 50),
          financePenaltyFixedAmount: valueToString(config.penaltyFixedAmount ?? 0),
        }));
        setFinanceLoading(false);
      })
      .catch((err) => {
        const backendMessage = err?.response?.data;
        setFinanceError(String(backendMessage || "No se pudo cargar el centro financiero del comercio"));
        setFinanceSummary(null);
        setFinanceLoading(false);
      });
  }, []);

  const getMerchants = useCallback(() => {
    setLoading(true);

    axios({
      method: "get",
      url: `${serverUrl}/merchants`,
      headers: buildAuthHeaders(),
    })
      .then((response) => {
        setMerchants(Array.isArray(response.data) ? response.data : []);
        setLoading(false);
      })
      .catch(() => {
        toast("No se pudieron cargar los comercios", darkToast);
        setLoading(false);
      });
  }, []);

  const getCountryPhoneCodes = useCallback(() => {
    setLoadingCountries(true);
    setCountriesError("");

    axios({
      method: "get",
      url: `${serverUrl}/countries/phone-codes`,
    })
      .then((response) => {
        const rows = Array.isArray(response.data?.data) ? response.data.data : [];
        const options = rows
          .map((item) => ({
            name: String(item.name || "").trim(),
            code: String(item.code || "").trim(),
            iso2: String(item.iso2 || "").trim(),
            label: String(item.label || "").trim(),
          }))
          .filter((item) => /^\+\d{1,6}$/.test(item.code) && item.label);

        setCountryOptions(options);
        setForm((current) => ({
          ...current,
          phoneCountryCode:
            current.phoneCountryCode ||
            options.find((option) => option.iso2 === "SV")?.code ||
            options[0]?.code ||
            "",
        }));
        setLoadingCountries(false);
      })
      .catch(() => {
        setCountriesError("No se pudieron cargar los códigos telefónicos");
        setLoadingCountries(false);
      });
  }, []);

  useEffect(() => {
    getMerchants();
    getCountryPhoneCodes();
  }, [getMerchants, getCountryPhoneCodes]);

  useEffect(() => {
    if (!selectedMerchantId) {
      setFinanceSummary(null);
      setFinanceError("");
      setActiveMerchantTab("perfil");
      return;
    }

    loadMerchantFinanceSummary(selectedMerchantId);
    loadPaymentRecipientProfileIntoForm(selectedMerchantId);
  }, [selectedMerchantId, loadMerchantFinanceSummary]);

  const updateForm = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateAvailabilityHour = (dayKey, field, value) => {
    setForm((current) => ({
      ...current,
      availabilityHours: {
        ...current.availabilityHours,
        [dayKey]: {
          ...(current.availabilityHours?.[dayKey] || defaultOperationalHours[dayKey]),
          [field]: field === "enabled" ? value === true : value,
        },
      },
    }));
  };

  const captureCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast("Este navegador no permite capturar ubicación GPS", darkToast);
      return;
    }

    setLocatingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = Number(position.coords.latitude).toFixed(6);
        const longitude = Number(position.coords.longitude).toFixed(6);
        const mapUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;

        setForm((current) => ({
          ...current,
          latitude,
          longitude,
          mapUrl,
          reference: current.reference || "Ubicación capturada por GPS del navegador",
        }));

        toast("Ubicación GPS capturada correctamente", darkToast);
        setLocatingLocation(false);
      },
      () => {
        toast("No se pudo capturar la ubicación. Revisa permisos del navegador.", darkToast);
        setLocatingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  const selectMerchantForEdit = (merchant) => {
    const merchantId = getMerchantId(merchant);
    const location = merchant?.location || {};
    const operationalSla = merchant?.metadata?.operationalSla || {};
    const operationalAvailability = merchant?.metadata?.operationalAvailability || {};
    const financeConfig = merchant?.metadata?.financeConfig || {};
    const paymentProfile =
      merchant?.paymentRecipientProfile ||
      merchant?.metadata?.paymentRecipientProfile ||
      merchant?.paymentProfile ||
      {};
    const countryCode = merchant?.phoneCountryCode || getDefaultCountryCode();
    const phoneLocalNumber =
      merchant?.phoneLocalNumber ||
      onlyDigits(String(merchant?.phone || "").replace(countryCode, ""));

    setSelectedMerchantId(merchantId);
    setForm({
      ...emptyForm,
      // PAGOS.REAL.2K.2B_HYDRATE_PAYMENT_PROFILE_ON_EDIT
      paymentProfileLegalName: paymentProfile.legalName || merchant?.legalName || merchant?.name || "",
      paymentProfileCommercialName: paymentProfile.commercialName || merchant?.name || "",
      paymentProfileDocumentType: paymentProfile.documentType || "",
      paymentProfileDocumentNumber: paymentProfile.documentNumber || "",
      paymentProfileNrc: paymentProfile.nrc || "",
      paymentProfileEmail: paymentProfile.email || merchant?.email || "",
      paymentProfilePhone: paymentProfile.phone || merchant?.phone || "",
      paymentProfileLegalRepresentativeName: paymentProfile.legalRepresentativeName || "",
      paymentProfileLegalRepresentativeDocument: paymentProfile.legalRepresentativeDocument || "",
      paymentProfileBankName: paymentProfile.bankName || "",
      paymentProfileBankAccountMasked: paymentProfile.bankAccountMasked || "",
      paymentProfileBankAccountHolder:
        paymentProfile.bankAccountHolder ||
        paymentProfile.legalName ||
        merchant?.legalName ||
        merchant?.name ||
        "",
      name: valueToString(merchant?.name),
      legalName: valueToString(merchant?.legalName),
      email: valueToString(merchant?.email),
      phone: valueToString(merchant?.phone),
      phoneCountryCode: countryCode,
      phoneLocalNumber,
      address: valueToString(location.address || merchant?.address),
      reference: valueToString(location.reference),
      latitude: valueToString(location.latitude),
      longitude: valueToString(location.longitude),
      mapUrl: valueToString(location.mapUrl),
      logoUrl: valueToString(merchant?.logoUrl),
      coverImageUrl: valueToString(merchant?.coverImageUrl),
      imageAlt: valueToString(merchant?.imageAlt),
      brandingStatus: merchant?.brandingStatus || "pending",
      status: merchant?.status || "pending",
      planKey: merchant?.planKey || "free",
      isActive: merchant?.isActive !== false,
      slaEnabled: operationalSla.enabled !== false,
      slaMerchantLimitMinutes: valueToString(operationalSla.merchantLimitMinutes || 25),
      slaWarningAfterMinutes: valueToString(operationalSla.warningAfterMinutes || 20),
      slaCourierReserveMinutes: valueToString(operationalSla.courierReserveMinutes || 10),
      slaPreparationMode:
        operationalSla.preparationMode === "automatic" || operationalSla.autoStartPreparationAfterAccept === true
          ? "automatic"
          : "manual",
      slaAutoStartPreparationAfterAccept:
        operationalSla.autoStartPreparationAfterAccept === true || operationalSla.preparationMode === "automatic",
      slaAutoStartPreparationDelaySeconds: valueToString(operationalSla.autoStartPreparationDelaySeconds || 10),
      slaAllowReadyForPickupFromAccepted: operationalSla.allowReadyForPickupFromAccepted === true,
      availabilityEnabled: operationalAvailability.enabled !== false,
      availabilityManualStatus: operationalAvailability.manualStatus || "open",
      availabilityMessage: valueToString(operationalAvailability.message),
      availabilityTimezone: operationalAvailability.timezone || "America/El_Salvador",
      availabilityHours: normalizeAvailabilityHours(
        operationalAvailability.hours || merchant?.metadata?.operationalHours || {}
      ),
      financeEnabled: financeConfig.enabled !== false,
      financeServiceType: financeConfig.serviceType || "delivery_food",
      financeCommissionPercent: valueToString(financeConfig.commissionPercent || 14),
      financeFixedCommissionAmount: valueToString(financeConfig.fixedCommissionAmount || 0),
      financePenaltyEnabled: financeConfig.penaltyEnabled !== false,
      financePenaltyAfterAcceptedMinutes: valueToString(financeConfig.penaltyAfterAcceptedMinutes || 10),
      financePenaltyPercent: valueToString(financeConfig.penaltyPercent || 50),
      financePenaltyFixedAmount: valueToString(financeConfig.penaltyFixedAmount || 0),
    });

    window.requestAnimationFrame(() => {
      // PAGOS.REAL.2K.2B: aunque el comercio ya venga con perfil en memoria,
    // se fuerza lectura real del endpoint maestro para evitar campos vacíos al salir/entrar.
    loadPaymentRecipientProfileIntoForm(merchantId);

    document.getElementById("merchant-registration-form")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const uploadMerchantBrandingImage = async (file, targetField) => {
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(String(file.type || "").toLowerCase())) {
      toast("Formato inválido. Usa JPG, PNG o WEBP.", darkToast);
      return;
    }

    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast("La imagen supera el máximo permitido de 20 MB.", darkToast);
      return;
    }

    const isLogo = targetField === "logoUrl";
    if (isLogo) setUploadingLogo(true);
    else setUploadingCover(true);

    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("folder", "merchants");

      const response = await axios.post(`${serverUrl}/upload/image`, formData, {
        headers: {
          Authorization: `JWT ${localStorage.getItem("token")}`,
        },
        timeout: 60000,
      });

      const url = response?.data?.url;
      if (!url) {
        throw new Error("El backend no devolvió la URL de la imagen");
      }

      setForm((current) => ({
        ...current,
        [targetField]: url,
        brandingStatus: "ready",
        imageAlt:
          current.imageAlt ||
          current.name ||
          "Imagen representativa del comercio",
      }));

      toast(
        isLogo ? "Logo subido correctamente" : "Imagen de presentación subida correctamente",
        darkToast
      );
    } catch (error) {
      const msg =
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.message ||
        "No se pudo subir la imagen";
      toast(String(msg), darkToast);
    } finally {
      if (isLogo) setUploadingLogo(false);
      else setUploadingCover(false);
    }
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      toast("El nombre comercial es obligatorio", darkToast);
      return false;
    }

    if (form.email && !form.email.includes("@")) {
      toast("El correo del comercio no parece válido", darkToast);
      return false;
    }

    if (!form.phoneCountryCode || !/^\+\d{1,6}$/.test(form.phoneCountryCode)) {
      toast("Selecciona un código telefónico válido", darkToast);
      return false;
    }

    if (!onlyDigits(form.phoneLocalNumber)) {
      toast("El número local del comercio es obligatorio", darkToast);
      return false;
    }

    if (form.latitude && toNullableNumber(form.latitude) === null) {
      toast("La latitud no es válida", darkToast);
      return false;
    }

    if (form.longitude && toNullableNumber(form.longitude) === null) {
      toast("La longitud no es válida", darkToast);
      return false;
    }

    return true;
  };

  const createMerchant = () => {
    if (!validateForm()) return;

    setSaving(true);

    const phoneLocalNumber = onlyDigits(form.phoneLocalNumber);
    const phone = `${form.phoneCountryCode}${phoneLocalNumber}`;

    const payload = {
      name: form.name,
      legalName: form.legalName,
      email: form.email,
      phone,
      phoneCountryCode: form.phoneCountryCode,
      phoneLocalNumber,
      address: form.address,
      location: {
        address: form.address,
        reference: form.reference,
        latitude: toNullableNumber(form.latitude),
        longitude: toNullableNumber(form.longitude),
        mapProvider: form.mapUrl ? "map_url" : "manual",
        mapUrl: form.mapUrl,
      },
      logoUrl: form.logoUrl,
      coverImageUrl: form.coverImageUrl,
      imageAlt: form.imageAlt,
      brandingStatus: form.brandingStatus || "pending",
      status: form.status,
      planKey: form.planKey,
      isActive: form.isActive,
    };

    const isEditing = Boolean(selectedMerchantId);

    axios({
      method: isEditing ? "patch" : "post",
      url: isEditing ? `${serverUrl}/merchants/${selectedMerchantId}` : `${serverUrl}/merchants`,
      headers: buildAuthHeaders(),
      data: payload,
    })
      .then((response) => {
        setMerchants((current) => {
          if (!isEditing) return [response.data, ...current];

          return current.map((merchant) =>
            getMerchantId(merchant) === selectedMerchantId ? response.data : merchant
          );
        });
        resetForm();
        toast(isEditing ? "Comercio actualizado correctamente" : "Comercio creado correctamente", darkToast);
        setSaving(false);
      })
      .catch((err) => {
        const backendMessage = err?.response?.data;
        toast(String(backendMessage || "No se pudo crear el comercio"), darkToast);
        setSaving(false);
      });
  };

  const saveFinanceConfig = () => {
    if (!selectedMerchantId) {
      toast("Selecciona un comercio antes de guardar configuración financiera", darkToast);
      return;
    }

    const commissionPercent = toPositiveNumberForm(form.financeCommissionPercent, 14);
    const fixedCommissionAmount = toPositiveNumberForm(form.financeFixedCommissionAmount, 0);
    const penaltyAfterAcceptedMinutes = toPositiveIntegerForm(form.financePenaltyAfterAcceptedMinutes, 10);
    const penaltyPercent = toPositiveNumberForm(form.financePenaltyPercent, 50);
    const penaltyFixedAmount = toPositiveNumberForm(form.financePenaltyFixedAmount, 0);

    if (commissionPercent < 0 || commissionPercent > 100) {
      toast("La comisión debe estar entre 0% y 100%", darkToast);
      return;
    }

    if (penaltyPercent < 0 || penaltyPercent > 100) {
      toast("La penalización debe estar entre 0% y 100%", darkToast);
      return;
    }

    if (penaltyAfterAcceptedMinutes < 1 || penaltyAfterAcceptedMinutes > 1440) {
      toast("La tolerancia de penalización debe estar entre 1 y 1440 minutos", darkToast);
      return;
    }

    const payload = {
      enabled: Boolean(form.financeEnabled),
      serviceType: form.financeServiceType || "delivery_food",
      commissionPercent,
      fixedCommissionAmount,
      penaltyEnabled: Boolean(form.financePenaltyEnabled),
      penaltyAfterAcceptedMinutes,
      penaltyPercent,
      penaltyFixedAmount,
    };

    setSavingFinanceConfig(true);

    axios
      .patch(`${serverUrl}/merchant-finance/admin/merchant/${selectedMerchantId}/config`, payload, {
        headers: buildAuthHeaders(),
      })
      .then(() => {
        toast("Configuración financiera guardada correctamente", darkToast);
        loadMerchantFinanceSummary(selectedMerchantId);
        setSavingFinanceConfig(false);
      })
      .catch((err) => {
        const backendMessage = err?.response?.data;
        toast(String(backendMessage || "No se pudo guardar configuración financiera"), darkToast);
        setSavingFinanceConfig(false);
      });
  };

  const saveOperationalSla = () => {
    if (!selectedMerchantId) {
      toast("Selecciona un comercio antes de guardar la configuración operativa", darkToast);
      return;
    }

    const merchantLimitMinutes = toPositiveIntegerForm(form.slaMerchantLimitMinutes, 25);
    const warningAfterMinutes = toPositiveIntegerForm(form.slaWarningAfterMinutes, 20);
    const courierReserveMinutes = toPositiveIntegerForm(form.slaCourierReserveMinutes, 10);
    const preparationMode = form.slaPreparationMode === "automatic" ? "automatic" : "manual";
    const autoStartPreparationAfterAccept = preparationMode === "automatic";
    const autoStartPreparationDelaySeconds = toPositiveIntegerForm(
      form.slaAutoStartPreparationDelaySeconds,
      10
    );
    const allowReadyForPickupFromAccepted = Boolean(form.slaAllowReadyForPickupFromAccepted);

    if (merchantLimitMinutes < 5 || merchantLimitMinutes > 180) {
      toast("El tiempo del comercio debe estar entre 5 y 180 minutos", darkToast);
      return;
    }

    if (warningAfterMinutes < 1 || warningAfterMinutes > merchantLimitMinutes) {
      toast("La alerta temprana debe ser mayor a 0 y no superar el tiempo del comercio", darkToast);
      return;
    }

    if (courierReserveMinutes < 1 || courierReserveMinutes > 120) {
      toast("La reserva del motorizado debe estar entre 1 y 120 minutos", darkToast);
      return;
    }

    if (
      preparationMode === "automatic" &&
      (autoStartPreparationDelaySeconds < 3 || autoStartPreparationDelaySeconds > 120)
    ) {
      toast("La espera automática debe estar entre 3 y 120 segundos", darkToast);
      return;
    }

    setSavingSla(true);

    const payload = {
      enabled: Boolean(form.slaEnabled),
      merchantLimitMinutes,
      warningAfterMinutes,
      courierReserveMinutes,
      preparationMode,
      autoStartPreparationAfterAccept,
      autoStartPreparationDelaySeconds,
      allowReadyForPickupFromAccepted,
    };

    axios
      .patch(`${serverUrl}/merchants/${selectedMerchantId}/operational-sla`, payload, {
        headers: buildAuthHeaders(),
      })
      .then((response) => {
        const updatedMerchant = response.data?.merchant;
        const operationalSla = response.data?.operationalSla || payload;

        if (updatedMerchant) {
          setMerchants((current) =>
            current.map((merchant) =>
              getMerchantId(merchant) === selectedMerchantId ? updatedMerchant : merchant
            )
          );
        }

        setForm((current) => ({
          ...current,
          slaEnabled: operationalSla.enabled !== false,
          slaMerchantLimitMinutes: valueToString(operationalSla.merchantLimitMinutes || 25),
          slaWarningAfterMinutes: valueToString(operationalSla.warningAfterMinutes || 20),
          slaCourierReserveMinutes: valueToString(operationalSla.courierReserveMinutes || 10),
          slaPreparationMode:
            operationalSla.preparationMode === "automatic" || operationalSla.autoStartPreparationAfterAccept === true
              ? "automatic"
              : "manual",
          slaAutoStartPreparationAfterAccept:
            operationalSla.autoStartPreparationAfterAccept === true || operationalSla.preparationMode === "automatic",
          slaAutoStartPreparationDelaySeconds: valueToString(operationalSla.autoStartPreparationDelaySeconds || 10),
          slaAllowReadyForPickupFromAccepted: operationalSla.allowReadyForPickupFromAccepted === true,
        }));

        toast("Configuración operativa SLA guardada correctamente", darkToast);
        setSavingSla(false);
      })
      .catch((err) => {
        const backendMessage = err?.response?.data;
        toast(String(backendMessage || "No se pudo guardar la configuración SLA"), darkToast);
        setSavingSla(false);
      });
  };

  const saveOperationalAvailability = () => {
    if (!selectedMerchantId) {
      toast("Selecciona un comercio antes de guardar la disponibilidad operativa", darkToast);
      return;
    }

    const payload = {
      enabled: Boolean(form.availabilityEnabled),
      manualStatus: form.availabilityManualStatus || "open",
      message: form.availabilityMessage || getDefaultAvailabilityMessage(form.availabilityManualStatus),
      timezone: form.availabilityTimezone || "America/El_Salvador",
      hours: normalizeAvailabilityHours(form.availabilityHours),
    };

    setSavingAvailability(true);

    axios
      .patch(`${serverUrl}/merchants/${selectedMerchantId}/operational-availability`, payload, {
        headers: buildAuthHeaders(),
      })
      .then((response) => {
        const updatedMerchant = response.data?.merchant;
        const operationalAvailability = response.data?.operationalAvailability || payload;

        if (updatedMerchant) {
          setMerchants((current) =>
            current.map((merchant) =>
              getMerchantId(merchant) === selectedMerchantId ? updatedMerchant : merchant
            )
          );
        }

        setForm((current) => ({
          ...current,
          availabilityEnabled: operationalAvailability.enabled !== false,
          availabilityManualStatus: operationalAvailability.manualStatus || "open",
          availabilityMessage: valueToString(operationalAvailability.message),
          availabilityTimezone: operationalAvailability.timezone || "America/El_Salvador",
          availabilityHours: normalizeAvailabilityHours(operationalAvailability.hours || {}),
        }));

        toast("Disponibilidad y horarios guardados correctamente", darkToast);
        setSavingAvailability(false);
      })
      .catch((err) => {
        const backendMessage = err?.response?.data;
        toast(String(backendMessage || "No se pudo guardar disponibilidad operativa"), darkToast);
        setSavingAvailability(false);
      });
  };

  const openCredentialPanel = (merchant, event) => {
    event?.stopPropagation();

    setCredentialPanel({
      open: true,
      loading: false,
      merchant,
      name: merchant?.name || "",
      email: merchant?.ownerUserId
        ? merchant?.email || getDefaultMerchantAccessUser(merchant)
        : getDefaultMerchantAccessUser(merchant),
      temporaryPassword: "",
      user: null,
      error: "",
      successMessage: "",
    });
  };

  const closeCredentialPanel = () => {
    setCredentialPanel({
      open: false,
      loading: false,
      merchant: null,
      name: "",
      email: "",
      temporaryPassword: "",
      user: null,
    });
  };

  const updateCredentialPanel = (field, value) => {
    setCredentialPanel((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const generateMerchantCredentials = async () => {
    const merchantId = getMerchantId(credentialPanel.merchant);

    if (!merchantId) {
      toast("Selecciona un comercio válido", darkToast);
      return;
    }

    if (!credentialPanel.email || !credentialPanel.email.includes("@")) {
      toast("El usuario de acceso debe tener formato tipo correo", darkToast);
      return;
    }

    setCredentialPanel((current) => ({
      ...current,
      loading: true,
      temporaryPassword: "",
    }));

    try {
      const response = await axios.post(
        `${serverUrl}/merchants/${merchantId}/credentials`,
        {
          name: credentialPanel.name || credentialPanel.merchant?.name || "",
          email: credentialPanel.email,
          password: "",
          isActive: true,
        },
        {
          headers: buildAuthHeaders(),
        }
      );

      const updatedMerchant = response.data?.merchant;
      const user = response.data?.user;
      const temporaryPassword = response.data?.temporaryPassword || "";

      if (updatedMerchant) {
        setMerchants((current) =>
          current.map((merchant) =>
            getMerchantId(merchant) === merchantId ? updatedMerchant : merchant
          )
        );
      }

      setCredentialPanel((current) => ({
        ...current,
        loading: false,
        merchant: updatedMerchant || current.merchant,
        user: user || null,
        temporaryPassword,
        error: "",
        successMessage: temporaryPassword
          ? "Acceso PWA creado. Copia estos datos y envíalos por WhatsApp."
          : "Acceso PWA actualizado correctamente.",
      }));

      toast("Credenciales PWA generadas correctamente", darkToast);
    } catch (err) {
      const backendMessage =
        err?.response?.data?.msg ||
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "No se pudo crear el acceso PWA";

      const readableError =
        typeof backendMessage === "string" ? backendMessage : JSON.stringify(backendMessage);

      setCredentialPanel((current) => ({
        ...current,
        loading: false,
        error: readableError,
        successMessage: "",
      }));

      toast(readableError, darkToast);
    }
  };

  const copyCredentialAccess = async () => {
    const email = credentialPanel.user?.email || credentialPanel.email;
    const password = credentialPanel.temporaryPassword;

    if (!email || !password) {
      toast("No hay credenciales temporales para copiar", darkToast);
      return;
    }

    const message = `Acceso PWA Super Agro Digital\n\nURL: https://comercios.superagrodig.com\nUsuario: ${email}\nContraseña temporal: ${password}\n\nPor seguridad, cambiaremos esta contraseña más adelante desde el panel.`;

    try {
      await navigator.clipboard.writeText(message);
      toast("Credenciales copiadas al portapapeles", darkToast);
    } catch {
      toast("No se pudo copiar automáticamente. Copia manualmente los datos visibles.", darkToast);
    }
  };

  const kpiCards = [
    { label: "Total comercios", value: stats.total, helper: "Base comercial registrada" },
    { label: "Activos", value: stats.active, helper: "Listos para operar" },
    { label: "Pendientes", value: stats.pending, helper: "En revisión operativa" },
    { label: "Suspendidos", value: stats.suspended, helper: "Requieren seguimiento" },
  ];

  return (
    <div className="content-body merchants-page">
      <div className="merchant-shell">
        <section className="merchant-hero">
          <div className="merchant-hero-content">
            <span className="merchant-module-pill">Módulo comercial</span>
            <h1>Comercios afiliados</h1>
            <p>
              Administra comercios, planes, contacto, ubicación y estado operativo desde una vista limpia,
              preparada para expansión comercial y automatización futura.
            </p>
          </div>

          <div className="merchant-hero-actions">
            <button type="button" className="merchant-refresh-button" onClick={getMerchants}>
              Actualizar comercios
            </button>
          </div>
        </section>

        <section className="merchant-kpi-grid">
          {kpiCards.map((item) => (
            <article className="merchant-kpi-card" key={item.label}>
              <div>
                <p className="merchant-kpi-label">{item.label}</p>
                <strong className="merchant-kpi-value">{item.value}</strong>
              </div>
              <span>{item.helper}</span>
            </article>
          ))}
        </section>

        <section className="merchant-control-panel">
          <div className="merchant-control-header">
            <div>
              <h2>Directorio de comercios</h2>
              <p>Filtra, revisa y valida comercios afiliados sin perder contexto operativo.</p>
            </div>
          </div>

          <div className="merchant-toolbar">
            <input
              type="text"
              placeholder="Buscar por nombre, razón social, correo o teléfono"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Todos los estados</option>
              <option value="pending">Pendientes</option>
              <option value="active">Activos</option>
              <option value="suspended">Suspendidos</option>
            </select>

            <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
              <option value="all">Todos los planes</option>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="premium">Premium</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {loading ? (
            <div className="merchant-empty-state">
              <ReactLoading type="spinningBubbles" color="var(--primaryColor)" width="4rem" height="4rem" />
              <p>Cargando comercios...</p>
            </div>
          ) : filteredMerchants.length === 0 ? (
            <div className="merchant-empty-state">
              <strong>No hay comercios para mostrar</strong>
              <p>Cuando registres comercios aparecerán aquí con su información operativa principal.</p>
            </div>
          ) : (
            <div className="merchant-card-grid">
              {filteredMerchants.map((merchant, index) => (
                <article
                  className={`merchant-card ${
                    selectedMerchantId === getMerchantId(merchant) ? "merchant-card-selected" : ""
                  }`}
                  key={merchant.id || merchant._id || index}
                  role="button"
                  tabIndex={0}
                  onClick={() => selectMerchantForEdit(merchant)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") selectMerchantForEdit(merchant);
                  }}
                >
                  <div className="merchant-card-cover">
                    {merchant.coverImageUrl ? (
                      <img
                        src={merchant.coverImageUrl}
                        alt={merchant.imageAlt || merchant.name || "Imagen del comercio"}
                      />
                    ) : (
                      <div className="merchant-card-cover-fallback">
                        <span>Imagen del comercio</span>
                      </div>
                    )}
                  </div>

                  <div className="merchant-card-top merchant-card-top-branding">
                    <div className="merchant-avatar merchant-logo-avatar">
                      {merchant.logoUrl ? (
                        <img src={merchant.logoUrl} alt={`Logo ${merchant.name || "comercio"}`} />
                      ) : (
                        getMerchantInitials(merchant.name)
                      )}
                    </div>
                    <div>
                      <h3>{merchant.name || "Comercio sin nombre"}</h3>
                      <p>{merchant.legalName || merchant.imageAlt || "Sin razón social registrada"}</p>
                    </div>
                    <button
                      type="button"
                      className="merchant-card-edit"
                      aria-label={`Editar ${merchant.name || "comercio"}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        selectMerchantForEdit(merchant);
                      }}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className={`merchant-credentials-button ${merchant.ownerUserId ? "merchant-credentials-ready" : ""}`}
                      onClick={(event) => openCredentialPanel(merchant, event)}
                      title="Crear o regenerar acceso PWA del comercio"
                    >
                      {merchant.ownerUserId ? "Acceso listo" : "Credenciales"}
                    </button>
                  </div>

                  <div className="merchant-card-meta">
                    <span>{getPhoneDisplay(merchant)}</span>
                    <a href={merchant.email ? `mailto:${merchant.email}` : undefined}>
                      {merchant.email || "Sin correo"}
                    </a>
                  </div>

                  <div className="merchant-card-footer">
                    <span className={getBadgeClass(merchant.status)}>
                      {statusLabels[merchant.status] || "Pendiente"}
                    </span>
                    <span className={getPlanClass(merchant.planKey)}>
                      {planLabels[merchant.planKey] || "Free"}
                    </span>
                    <small>{formatDate(merchant.createdAt)}</small>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="merchant-registration-panel" id="merchant-registration-form">
          <div className="merchant-form-intro">
            <span>{selectedMerchantId ? "Edición comercial" : "Registro comercial"}</span>
            <h2>{selectedMerchantId ? `Actualizar ${form.name || "comercio"}` : "Registrar comercio"}</h2>
            <p>
              {selectedMerchantId
                ? "Modifica la información del comercio seleccionado y guarda los cambios."
                : "Captura información limpia, útil y preparada para operación, ubicación y crecimiento."}
            </p>
          </div>

          <form
            className="merchant-premium-form"
            onSubmit={(event) => {
              event.preventDefault();
              createMerchant();
            }}
          >
            <div className="merchant-form-section">
              <div className="merchant-section-title">
                <span>01</span>
                <div>
                  <h3>Información comercial</h3>
                  <p>Datos principales del comercio afiliado.</p>
                </div>
              </div>

              <div className="merchant-field-grid">
                <label>
                  Nombre comercial
                  <input
                    type="text"
                    placeholder="Ej. Restaurante La Esquina"
                    value={form.name}
                    onChange={(e) => updateForm("name", e.target.value)}
                  />
                </label>

                <label>
                  Razón social
                  <input
                    type="text"
                    placeholder="Nombre legal o empresa"
                    value={form.legalName}
                    onChange={(e) => updateForm("legalName", e.target.value)}
                  />
                </label>
              </div>
            </div>

            <div className="merchant-form-section">
              <div className="merchant-section-title">
                <span>02</span>
                <div>
                  <h3>Contacto</h3>
                  <p>Correo y teléfono operativo del comercio.</p>
                </div>
              </div>

              <div className="merchant-field-grid">
                <label>
                  Correo
                  <input
                    type="email"
                    placeholder="correo@comercio.com"
                    value={form.email}
                    onChange={(e) => updateForm("email", e.target.value)}
                  />
                </label>

                <label>
                  Teléfono
                  <div className="merchant-phone-row">
                    <select
                      value={form.phoneCountryCode}
                      onChange={(e) => updateForm("phoneCountryCode", e.target.value)}
                      disabled={loadingCountries}
                    >
                      <option value="">
                        {loadingCountries ? "Cargando países..." : "Seleccionar país"}
                      </option>
                      {countryOptions.map((option) => (
                        <option value={option.code} key={`${option.iso2}-${option.code}`}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                    <input
                      type="text"
                      placeholder="Número local"
                      value={form.phoneLocalNumber}
                      onChange={(e) => updateForm("phoneLocalNumber", onlyDigits(e.target.value))}
                    />
                  </div>

                  {countriesError ? (
                    <button type="button" className="merchant-link-button" onClick={getCountryPhoneCodes}>
                      Reintentar cargar países
                    </button>
                  ) : null}
                </label>
              </div>
            </div>

            <div className="merchant-form-section">
              <div className="merchant-section-title">
                <span>03</span>
                <div>
                  <h3>Identidad visual</h3>
                  <p>Logo y portada comercial del negocio. Usa imágenes limpias, elegantes y representativas del giro.</p>
                </div>
              </div>

              <div className="merchant-branding-grid">
                <div className="merchant-upload-card merchant-logo-upload">
                  <div className="merchant-upload-preview">
                    {form.logoUrl ? (
                      <img src={form.logoUrl} alt="Logo del comercio" />
                    ) : (
                      <span>Logo</span>
                    )}
                  </div>

                  <label>
                    Logo del comercio
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(event) => uploadMerchantBrandingImage(event.target.files?.[0], "logoUrl")}
                    />
                  </label>

                  <small>
                    {uploadingLogo ? "Subiendo logo..." : "Formato recomendado: cuadrado, limpio y legible."}
                  </small>
                </div>

                <div className="merchant-upload-card merchant-cover-upload">
                  <div className="merchant-upload-preview">
                    {form.coverImageUrl ? (
                      <img src={form.coverImageUrl} alt="Imagen de presentación del comercio" />
                    ) : (
                      <span>Portada</span>
                    )}
                  </div>

                  <label>
                    Imagen de presentación
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(event) => uploadMerchantBrandingImage(event.target.files?.[0], "coverImageUrl")}
                    />
                  </label>

                  <small>
                    {uploadingCover
                      ? "Subiendo imagen..."
                      : "Debe representar el giro: platos, productos, local o experiencia comercial."}
                  </small>
                </div>
              </div>

              <div className="merchant-field-grid merchant-branding-fields">
                <label className="merchant-field-full">
                  Texto alternativo / descripción visual
                  <input
                    type="text"
                    placeholder="Ej. Pollo asado, carne asada y tajadas de plátano"
                    value={form.imageAlt}
                    onChange={(e) => updateForm("imageAlt", e.target.value)}
                  />
                </label>

                <label>
                  Estado de branding
                  <select value={form.brandingStatus} onChange={(e) => updateForm("brandingStatus", e.target.value)}>
                    <option value="pending">Pendiente</option>
                    <option value="ready">Listo</option>
                    <option value="review">En revisión</option>
                    <option value="rejected">Rechazado</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="merchant-form-section">
              <div className="merchant-section-title">
                <span>04</span>
                <div>
                  <h3>Ubicación GPS</h3>
                  <p>Captura coordenadas exactas desde el navegador para reducir errores manuales.</p>
                </div>
              </div>

              <div className="merchant-location-tools">
                <button type="button" onClick={captureCurrentLocation} disabled={locatingLocation}>
                  {locatingLocation ? "Capturando GPS..." : "Usar GPS actual"}
                </button>
                {form.latitude && form.longitude ? (
                  <a href={form.mapUrl} target="_blank" rel="noreferrer">
                    Abrir mapa
                  </a>
                ) : null}
              </div>

              <div className="merchant-field-grid">
                <label className="merchant-field-full">
                  Dirección / referencia comercial
                  <input
                    type="text"
                    placeholder="Dirección visible o referencia comercial"
                    value={form.address}
                    onChange={(e) => updateForm("address", e.target.value)}
                  />
                </label>

                <label className="merchant-field-full">
                  Referencia
                  <input
                    type="text"
                    placeholder="Ej. frente al parque, local 2"
                    value={form.reference}
                    onChange={(e) => updateForm("reference", e.target.value)}
                  />
                </label>

                <label>
                  Latitud
                  <input
                    type="text"
                    placeholder="13.6929"
                    value={form.latitude}
                    onChange={(e) => updateForm("latitude", e.target.value)}
                  />
                </label>

                <label>
                  Longitud
                  <input
                    type="text"
                    placeholder="-89.2182"
                    value={form.longitude}
                    onChange={(e) => updateForm("longitude", e.target.value)}
                  />
                </label>

                <label className="merchant-field-full">
                  URL de mapa
                  <input
                    type="text"
                    placeholder="Google Maps, Waze u OpenStreetMap"
                    value={form.mapUrl}
                    onChange={(e) => updateForm("mapUrl", e.target.value)}
                  />
                </label>
              </div>
            </div>

            <div className="merchant-form-section">
              <div className="merchant-section-title">
                <span>05</span>
                <div>
                  <h3>Operación</h3>
                  <p>Estado, plan comercial y acceso del comercio.</p>
                </div>
              </div>

              <div className="merchant-field-grid">
                <label>
                  Estado operativo
                  <select value={form.status} onChange={(e) => updateForm("status", e.target.value)}>
                    <option value="pending">Pendiente</option>
                    <option value="active">Activo</option>
                    <option value="suspended">Suspendido</option>
                  </select>
                </label>

                <label>
                  Plan comercial
                  <select value={form.planKey} onChange={(e) => updateForm("planKey", e.target.value)}>
                    <option value="free">Free</option>
                    <option value="pro">Pro</option>
                    <option value="premium">Premium</option>
                    <option value="custom">Custom</option>
                  </select>
                </label>

                <label>
                  Acceso
                  <select
                    value={form.isActive ? "active" : "inactive"}
                    onChange={(e) => updateForm("isActive", e.target.value === "active")}
                  >
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="merchant-form-section">
              <div className="merchant-section-title">
                <span>06</span>
                <div>
                  <h3>Configuración operativa / SLA</h3>
                  <p>
                    Define el tiempo máximo del comercio y la alerta temprana para que la PWA muestre un semáforo operativo dinámico.
                  </p>
                </div>
              </div>

              <div className="merchant-field-grid">
                <label>
                  Semáforo operativo
                  <select
                    value={form.slaEnabled ? "active" : "inactive"}
                    onChange={(e) => updateForm("slaEnabled", e.target.value === "active")}
                    disabled={!selectedMerchantId}
                  >
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                  </select>
                </label>

                <label>
                  Tiempo comercio, minutos
                  <input
                    type="number"
                    min="5"
                    max="180"
                    value={form.slaMerchantLimitMinutes}
                    onChange={(e) => updateForm("slaMerchantLimitMinutes", e.target.value)}
                    disabled={!selectedMerchantId}
                  />
                </label>

                <label>
                  Alerta temprana, minuto
                  <input
                    type="number"
                    min="1"
                    max="180"
                    value={form.slaWarningAfterMinutes}
                    onChange={(e) => updateForm("slaWarningAfterMinutes", e.target.value)}
                    disabled={!selectedMerchantId}
                  />
                </label>

                <label>
                  Reserva motorizado, minutos
                  <input
                    type="number"
                    min="1"
                    max="120"
                    value={form.slaCourierReserveMinutes}
                    onChange={(e) => updateForm("slaCourierReserveMinutes", e.target.value)}
                    disabled={!selectedMerchantId}
                  />
                </label>

                <label>
                  Modo de preparación
                  <select
                    value={form.slaPreparationMode}
                    onChange={(e) => {
                      const mode = e.target.value === "automatic" ? "automatic" : "manual";

                      updateForm("slaPreparationMode", mode);
                      updateForm("slaAutoStartPreparationAfterAccept", mode === "automatic");
                    }}
                  >
                    <option value="manual">Manual guiado</option>
                    <option value="automatic">Automático después de aceptar</option>
                  </select>
                  <small>
                    Manual: el comercio presiona Preparando. Automático: al aceptar, pasa a preparación después del tiempo configurado.
                  </small>
                </label>

                <label>
                  Espera automática después de aceptar (segundos)
                  <input
                    type="number"
                    min="3"
                    max="120"
                    placeholder="10"
                    value={form.slaAutoStartPreparationDelaySeconds}
                    disabled={form.slaPreparationMode !== "automatic"}
                    onChange={(e) => updateForm("slaAutoStartPreparationDelaySeconds", e.target.value)}
                  />
                  <small>
                    Recomendado: 10 segundos para que el comercio vea la transición antes de pasar a Preparando.
                  </small>
                </label>

                <label>
                  Permitir salto directo a listo
                  <select
                    value={form.slaAllowReadyForPickupFromAccepted ? "yes" : "no"}
                    onChange={(e) => updateForm("slaAllowReadyForPickupFromAccepted", e.target.value === "yes")}
                  >
                    <option value="no">No, debe pasar primero por Preparando</option>
                    <option value="yes">Sí, permitir salto rápido controlado</option>
                  </select>
                  <small>
                    Por defecto debe quedar en No para evitar errores operativos del comercio.
                  </small>
                </label>
              </div>

              <div className="merchant-location-tools">
                <button
                  type="button"
                  onClick={saveOperationalSla}
                  disabled={savingSla || !selectedMerchantId}
                >
                  {savingSla ? "Guardando SLA..." : "Guardar configuración SLA"}
                </button>
                <span>
                  {selectedMerchantId
                    ? "Valores usados por la PWA para contador, semáforo y alerta al comercio."
                    : "Crea o selecciona un comercio para configurar su SLA operativo."}
                </span>
              </div>
            </div>

            <div className="merchant-form-section">
              <div className="merchant-section-title">
                <span>07</span>
                <div>
                  <h3>Horarios y disponibilidad</h3>
                  <p>Define cuándo el comercio está disponible para operar. Esta base será consumida por la App Cliente más adelante.</p>
                </div>
              </div>

              <div className="merchant-field-grid">
                <label>
                  Control de disponibilidad
                  <select
                    value={form.availabilityEnabled ? "active" : "inactive"}
                    onChange={(e) => updateForm("availabilityEnabled", e.target.value === "active")}
                    disabled={!selectedMerchantId}
                  >
                    <option value="active">Activo con horario</option>
                    <option value="inactive">Sin restricción temporal</option>
                  </select>
                </label>

                <label>
                  Estado operativo manual
                  <select
                    value={form.availabilityManualStatus}
                    onChange={(e) => {
                      const nextStatus = e.target.value;

                      updateForm("availabilityManualStatus", nextStatus);
                      updateForm("availabilityMessage", getDefaultAvailabilityMessage(nextStatus));
                    }}
                    disabled={!selectedMerchantId}
                  >
                    <option value="open">Abierto</option>
                    <option value="paused">Cerrado</option>
                    <option value="saturated">Saturado</option>
                    <option value="temporarily_closed">Cerrado temporal</option>
                  </select>
                </label>

                <label>
                  Zona horaria
                  <select
                    value={form.availabilityTimezone}
                    onChange={(e) => updateForm("availabilityTimezone", e.target.value)}
                    disabled={!selectedMerchantId}
                  >
                    <option value="America/El_Salvador">El Salvador</option>
                  </select>
                </label>

                <label className="merchant-field-full">
                  Mensaje operativo visible
                  <input
                    type="text"
                    placeholder="Ej. Comercio saturado en este momento. Intenta más tarde."
                    value={form.availabilityMessage}
                    onChange={(e) => updateForm("availabilityMessage", e.target.value)}
                    disabled={!selectedMerchantId}
                  />
                </label>
              </div>

              <div className="merchant-hours-grid">
                {operationalDayLabels.map(([dayKey, label]) => {
                  const day = form.availabilityHours?.[dayKey] || defaultOperationalHours[dayKey];

                  return (
                    <div className="merchant-hour-card" key={dayKey}>
                      <div className="merchant-hour-card-header">
                        <strong>{label}</strong>
                        <select
                          value={day.enabled ? "open" : "closed"}
                          onChange={(e) => updateAvailabilityHour(dayKey, "enabled", e.target.value === "open")}
                          disabled={!selectedMerchantId}
                        >
                          <option value="open">Abierto</option>
                          <option value="closed">Cerrado</option>
                        </select>
                      </div>

                      <div className="merchant-hour-inputs">
                        <label>
                          Desde
                          <input
                            type="time"
                            value={day.open}
                            onChange={(e) => updateAvailabilityHour(dayKey, "open", e.target.value)}
                            disabled={!selectedMerchantId || !day.enabled}
                          />
                        </label>

                        <label>
                          Hasta
                          <input
                            type="time"
                            value={day.close}
                            onChange={(e) => updateAvailabilityHour(dayKey, "close", e.target.value)}
                            disabled={!selectedMerchantId || !day.enabled}
                          />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="merchant-location-tools">
                <button
                  type="button"
                  onClick={saveOperationalAvailability}
                  disabled={savingAvailability || !selectedMerchantId}
                >
                  {savingAvailability ? "Guardando disponibilidad..." : "Guardar horarios y disponibilidad"}
                </button>
                <span>
                  {selectedMerchantId
                    ? "Estos valores preparan el bloqueo futuro en App Cliente y el estado visible del comercio."
                    : "Selecciona un comercio para configurar sus horarios."}
                </span>
              </div>
            </div>

            {selectedMerchantId ? (
              <div className="merchant-form-section merchant-payment-profile-master">
                <div className="merchant-section-title">
                  <span>08</span>
                  <div>
                    <h3>Ficha legal y bancaria del comercio</h3>
                    <p>
                      {/* PAGOS.REAL.2J1_PROFILE_SECTION_OUTSIDE_PAYMENT */}
                      Estos datos pertenecen al comercio. El recibo de pago los hereda automáticamente y los congela como snapshot.
                    </p>
                  </div>
                </div>

                <div className="merchant-field-grid">
                  <label>
                    Nombre / razón social
                    <input
                      type="text"
                      value={form.paymentProfileLegalName}
                      onChange={(e) => updateForm("paymentProfileLegalName", e.target.value)}
                      placeholder="Razón social o nombre legal"
                    />
                  </label>

                  <label>
                    Nombre comercial
                    <input
                      type="text"
                      value={form.paymentProfileCommercialName}
                      onChange={(e) => updateForm("paymentProfileCommercialName", e.target.value)}
                      placeholder="Nombre público del comercio"
                    />
                  </label>

                  <label>
                    Tipo documento
                    <select
                      value={form.paymentProfileDocumentType}
                      onChange={(e) => updateForm("paymentProfileDocumentType", e.target.value)}
                    >
                      <option value="">Seleccionar</option>
                      <option value="DUI">DUI</option>
                      <option value="NIT">NIT</option>
                      <option value="NRC">NRC</option>
                      <option value="PASAPORTE">Pasaporte</option>
                      <option value="OTRO">Otro</option>
                    </select>
                  </label>

                  <label>
                    Número documento / NIT
                    <input
                      type="text"
                      value={form.paymentProfileDocumentNumber}
                      onChange={(e) => updateForm("paymentProfileDocumentNumber", e.target.value)}
                      placeholder="DUI, NIT o documento fiscal"
                    />
                  </label>

                  <label>
                    NRC
                    <input
                      type="text"
                      value={form.paymentProfileNrc}
                      onChange={(e) => updateForm("paymentProfileNrc", e.target.value)}
                      placeholder="NRC si aplica"
                    />
                  </label>

                  <label>
                    Correo
                    <input
                      type="email"
                      value={form.paymentProfileEmail}
                      onChange={(e) => updateForm("paymentProfileEmail", e.target.value)}
                      placeholder="Correo del comercio"
                    />
                  </label>

                  <label>
                    Teléfono
                    <input
                      type="text"
                      value={form.paymentProfilePhone}
                      onChange={(e) => updateForm("paymentProfilePhone", e.target.value)}
                      placeholder="Teléfono del comercio"
                    />
                  </label>

                  <label>
                    Representante legal
                    <input
                      type="text"
                      value={form.paymentProfileLegalRepresentativeName}
                      onChange={(e) => updateForm("paymentProfileLegalRepresentativeName", e.target.value)}
                      placeholder="Representante legal si aplica"
                    />
                  </label>

                  <label>
                    Documento representante
                    <input
                      type="text"
                      value={form.paymentProfileLegalRepresentativeDocument}
                      onChange={(e) => updateForm("paymentProfileLegalRepresentativeDocument", e.target.value)}
                      placeholder="DUI/NIT del representante"
                    />
                  </label>

                  <label>
                    Banco destino
                    <input
                      type="text"
                      value={form.paymentProfileBankName}
                      onChange={(e) => updateForm("paymentProfileBankName", e.target.value)}
                      placeholder="Banco donde se deposita"
                    />
                  </label>

                  <label>
                    Cuenta destino
                    <input
                      type="text"
                      value={form.paymentProfileBankAccountMasked}
                      onChange={(e) => updateForm("paymentProfileBankAccountMasked", e.target.value)}
                      placeholder="Cuenta o alias del comercio"
                    />
                  </label>

                  <label>
                    Titular de cuenta
                    <input
                      type="text"
                      value={form.paymentProfileBankAccountHolder}
                      onChange={(e) => updateForm("paymentProfileBankAccountHolder", e.target.value)}
                      placeholder="Titular de la cuenta bancaria"
                    />
                  </label>
                </div>

                <div className="merchant-location-tools">
                  <button
                    type="button"
                    onClick={savePaymentRecipientProfileFromForm}
                    disabled={savingPaymentRecipientProfile}
                  >
                    {savingPaymentRecipientProfile ? "Guardando ficha..." : "Guardar ficha legal/bancaria"}
                  </button>
                  <span>
                    Esta ficha se guarda una vez por comercio y se hereda automáticamente en los pagos.
                  </span>
                </div>
              </div>
            ) : null}

            {selectedMerchantId ? (
              <div className="merchant-form-section merchant-ops-center">
                <div className="merchant-section-title">
                  <span>07</span>
                  <div>
                    <h3>Centro operativo del comercio</h3>
                    <p>Administra operación, finanzas, pagos, penalizaciones y alertas desde una sola tarjeta.</p>
                  </div>
                </div>

                <div className="merchant-ops-header">
                  <div>
                    <span className="merchant-ops-kicker">Panel premium</span>
                    <h3>{form.name || "Comercio seleccionado"}</h3>
                    <p>
                      Alertas activas, solicitudes de pago, incidencias SLA y saldo operativo en tiempo real.
                    </p>
                  </div>

                  <div className={`merchant-ops-bell ${getFinanceAlertCount(financeSummary) > 0 ? "merchant-ops-bell-active" : ""}`}>
                    <span>🔔</span>
                    <strong>{getFinanceAlertCount(financeSummary)}</strong>
                    <small>Alertas</small>
                  </div>
                </div>

                <div className="merchant-ops-tabs">
                  {[
                    ["perfil", "Perfil"],
                    ["operacion", "Operación / SLA"],
                    ["finanzas", "Finanzas"],
                    ["pagos", "Pagos"],
                    ["incidencias", "Incidencias"],
                  ].map(([key, label]) => (
                    <button
                      type="button"
                      key={key}
                      className={`merchant-ops-tab ${activeMerchantTab === key ? "merchant-ops-tab-active" : ""}`}
                      onClick={() => setActiveMerchantTab(key)}
                    >
                      {label}
                    </button>
                  ))}
                
                  <button
                    type="button"
                    className={`merchant-ops-tab ${activeMerchantTab === "multimedia" ? "merchant-ops-tab-active" : ""}`}
                    onClick={() => setActiveMerchantTab("multimedia")}
                  >
                    <span>Multimedia</span>
                    <strong>Vitrina</strong>
                  </button>
</div>

                <MerchantMediaAdminPanel
                  merchantId={selectedMerchantId}
                  merchantName={form.name || form.legalName || "Comercio seleccionado"}
                  authConfig={getAdminAuthConfig()}
                  visible={activeMerchantTab === "multimedia"}
                />

                {financeLoading ? (
                  <div className="merchant-finance-loading">
                    <ReactLoading type="spinningBubbles" color="var(--primaryColor)" width="2.5rem" height="2.5rem" />
                    <span>Cargando centro financiero...</span>
                  </div>
                ) : financeError ? (
                  <div className="merchant-finance-error">{financeError}</div>
                ) : (
                  <>
                    {activeMerchantTab === "perfil" ? (
                      <div className="merchant-ops-placeholder">
                        <strong>Perfil operativo conectado</strong>
                        <p>
                          Usa las secciones superiores para editar datos comerciales, identidad visual, GPS y credenciales PWA.
                        </p>
                      </div>
                    ) : null}

                    {activeMerchantTab === "operacion" ? (
                      <div className="merchant-ops-placeholder">
                        <strong>Operación / SLA configurado</strong>
                        <p>
                          El SLA operativo se administra en la sección 06. Sus valores alimentan la PWA del comercio en tiempo real.
                        </p>
                      </div>
                    ) : null}

                    {activeMerchantTab === "finanzas" ? (
                      <div className="merchant-finance-panel">
                        <div className="merchant-finance-kpis">
                          <div>
                            <span>Ventas brutas</span>
                            <strong>{formatMoney(financeSummary?.summary?.grossAmount || 0)}</strong>
                          </div>
                          <div>
                            <span>Comisión plataforma</span>
                            <strong>{formatMoney(financeSummary?.summary?.commissionAmount || 0)}</strong>
                          </div>
                          <div>
                            <span>Penalizaciones</span>
                            <strong>{formatMoney(financeSummary?.summary?.penaltyAmount || 0)}</strong>
                          </div>
                          <div className={(financeSummary?.summary?.availableBalance || 0) < 0 ? "merchant-finance-negative" : ""}>
                            <span>Saldo disponible</span>
                            <strong>{formatMoney(financeSummary?.summary?.availableBalance || 0)}</strong>
                          </div>
                        </div>

                        <div className="merchant-finance-config">
                          <h4>Configuración económica</h4>
                          <div className="merchant-field-grid">
                            <label>
                              Comisión activa
                              <select
                                value={form.financeEnabled ? "active" : "inactive"}
                                onChange={(e) => updateForm("financeEnabled", e.target.value === "active")}
                              >
                                <option value="active">Activa</option>
                                <option value="inactive">Inactiva</option>
                              </select>
                            </label>

                            <label>
                              Tipo de servicio
                              <select
                                value={form.financeServiceType}
                                onChange={(e) => updateForm("financeServiceType", e.target.value)}
                              >
                                <option value="delivery_food">Delivery comida</option>
                                <option value="delivery_product">Delivery producto</option>
                                <option value="moto_person">Moto personas</option>
                                <option value="taxi">Taxi</option>
                                <option value="professional_service">Servicio profesional</option>
                                <option value="other">Otro</option>
                              </select>
                            </label>

                            <label>
                              Comisión %
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={form.financeCommissionPercent}
                                onChange={(e) => updateForm("financeCommissionPercent", e.target.value)}
                              />
                            </label>

                            <label>
                              Comisión fija
                              <input
                                type="number"
                                min="0"
                                value={form.financeFixedCommissionAmount}
                                onChange={(e) => updateForm("financeFixedCommissionAmount", e.target.value)}
                              />
                            </label>

                            <label>
                              Penalización activa
                              <select
                                value={form.financePenaltyEnabled ? "active" : "inactive"}
                                onChange={(e) => updateForm("financePenaltyEnabled", e.target.value === "active")}
                              >
                                <option value="active">Activa</option>
                                <option value="inactive">Inactiva</option>
                              </select>
                            </label>

                            <label>
                              Tolerancia después de aceptar, min
                              <input
                                type="number"
                                min="1"
                                max="1440"
                                value={form.financePenaltyAfterAcceptedMinutes}
                                onChange={(e) => updateForm("financePenaltyAfterAcceptedMinutes", e.target.value)}
                              />
                            </label>

                            <label>
                              Penalización %
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={form.financePenaltyPercent}
                                onChange={(e) => updateForm("financePenaltyPercent", e.target.value)}
                              />
                            </label>

                            <label>
                              Penalización fija
                              <input
                                type="number"
                                min="0"
                                value={form.financePenaltyFixedAmount}
                                onChange={(e) => updateForm("financePenaltyFixedAmount", e.target.value)}
                              />
                            </label>
                          </div>

                          <button
                            type="button"
                            className="merchant-primary-button merchant-finance-save"
                            onClick={saveFinanceConfig}
                            disabled={savingFinanceConfig}
                          >
                            {savingFinanceConfig ? "Guardando finanzas..." : "Guardar configuración financiera"}
                          </button>
                        </div>

                        <div className="merchant-ledger-intro">
                          <div>
                            <span>Movimientos financieros / Ledger</span>
                            <h4>Base del saldo del comercio</h4>
                            <p>
                              Este grid no es una solicitud de pago. Es el historial contable que calcula ventas,
                              comisiones, penalizaciones y el neto disponible para liquidación.
                            </p>
                          </div>
                          <strong>
                            Saldo actual: {formatMoney(financeSummary?.summary?.availableBalance || 0)}
                          </strong>
                        </div>

                        <div className="merchant-modern-grid merchant-ledger-grid">
                          <div className="merchant-modern-grid-head">
                            <span>Movimiento</span>
                            <span>Orden</span>
                            <span>Estado ledger</span>
                            <span>Neto</span>
                          </div>
                          {(financeSummary?.recentLedger || []).slice(0, 8).map((row) => (
                            <div className="merchant-modern-grid-row" key={row.id}>
                              <span>{getLedgerTypeLabel(row.type)}</span>
                              <span>#{row.orderId || "-"}</span>
                              <span>{getLedgerStatusLabel(row.status)}</span>
                              <strong>{formatMoney(row.netAmount || 0)}</strong>
                            </div>
                          ))}
                          {!(financeSummary?.recentLedger || []).length ? (
                            <div className="merchant-modern-grid-empty">Sin movimientos financieros todavía.</div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                      {activeMerchantTab === "pagos" ? (
                        <div className="merchant-payout-panel">
                          <div className="merchant-payout-explainer">
                            <div>
                              <span>Liquidaciones del comercio</span>
                              <h4>Cómo se paga al comercio</h4>
                              <p>
                                El comercio solicita pago desde su PWA cuando tiene saldo disponible. Admin revisa,
                                aprueba, paga por transferencia u otro método, registra referencia/comprobante y deja
                                evidencia de recepción.
                              </p>
                            </div>
                            <div className="merchant-payout-balance-summary">
                              <strong className={(financeSummary?.summary?.availableBalance || 0) > 0 || getRealPendingPayoutBalance(financeSummary) > 0 ? "" : "merchant-payout-blocked"}>
                                {(financeSummary?.summary?.availableBalance || 0) > 0
                                  ? `Disponible para solicitud: ${formatMoney(financeSummary?.summary?.availableBalance || 0)}`
                                  : getRealPendingPayoutBalance(financeSummary) > 0
                                    ? `Saldo solicitado pendiente: ${formatMoney(getRealPendingPayoutBalance(financeSummary))}`
                                    : `Sin saldo disponible: ${formatMoney(financeSummary?.summary?.availableBalance || 0)}`}
                              </strong>

                              {getRealPendingPayoutBalance(financeSummary) > 0 ? (
                                <small>
                                  El saldo del comercio ya fue solicitado y está pendiente de revisión/pago Admin.
                                </small>
                              ) : null}

                              {getQaTracePayoutCount(financeSummary) > 0 ? (
                                <small className="merchant-payout-qa-note">
                                  Escenarios QA visibles: {getQaTracePayoutCount(financeSummary)}. No son pagos reales.
                                </small>
                              ) : null}
                            </div>
                          </div>

                          <div className="merchant-payout-flow">
                            <div><span>01</span><strong>Solicitado</strong><small>El comercio pide liquidación.</small></div>
                            <div><span>02</span><strong>En revisión</strong><small>Admin valida saldo, órdenes y penalizaciones.</small></div>
                            <div><span>03</span><strong>Aprobado</strong><small>Se confirma el monto a pagar.</small></div>
                            <div><span>04</span><strong>Pagado</strong><small>Transferencia, referencia y comprobante.</small></div>
                            <div><span>05</span><strong>Recibido</strong><small>Comercio confirma recepción.</small></div>
                          </div>

                          <div className="merchant-payout-admin-note">
                            <strong>Soporte administrativo requerido</strong>
                            <p>
                              Cada solicitud conserva monto solicitado, aprobado, método, banco, referencia,
                              comprobante/evidencia, responsable, SLA, usuario admin y timeline de auditoría.
                            </p>
                          </div>

                          {/* PAGOS.REAL.2D3_WIRE_CASEFILE_PROPS */}
                          <MerchantPayoutAdminPanel
                            financeSummary={financeSummary}
                            payoutBusyId={payoutActionBusy}
                            onPayoutStatusChange={updatePayoutStatus}
                            onPayoutProofUpload={uploadPayoutProofFile}
                            onRefresh={() => loadMerchantFinanceSummary(selectedMerchantId)}
                          />
                        </div>
                      ) : null}

                    {activeMerchantTab === "incidencias" ? (
                      <div className="merchant-ops-placeholder merchant-incident-summary">
                        <strong>{financeSummary?.openIncidents || 0} incidencias abiertas</strong>
                        <p>
                          Las incidencias SLA se generan desde backend lifecycle cuando una orden supera la tolerancia
                          configurada. Cada incidencia puede crear una penalización, un movimiento ledger y un evento
                          de auditoría asociado a la orden.
                        </p>
                        <span>Penalización acumulada: {formatMoney(financeSummary?.summary?.penaltyAmount || 0)}</span>
                        <small>
                          La gestión avanzada de incidencias permitirá resolver, reversar o mantener penalizaciones con trazabilidad.
                        </small>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}

            <div className="merchant-actions">
              <button type="submit" className="merchant-primary-action" disabled={saving}>
                {saving ? (
                  <ReactLoading type="spinningBubbles" color="white" width="1.2rem" height="1.2rem" />
                ) : (
                  selectedMerchantId ? "Guardar cambios" : "Crear comercio"
                )}
              </button>

              <button type="button" className="merchant-secondary-action" onClick={resetForm}>
                {selectedMerchantId ? "Cancelar edición" : "Limpiar"}
              </button>
            </div>
          </form>
        </section>
      </div>

        {payoutDetail ? (
          <div className="merchant-payout-detail-overlay" role="presentation" onClick={() => setPayoutDetail(null)}>
            <section
              className="merchant-payout-detail-panel"
              role="dialog"
              aria-modal="true"
              aria-label="Detalle de solicitud de pago"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="merchant-payout-detail-header">
                <div>
                  <span>Detalle financiero</span>
                  <h3>{payoutDetail.requestNumber}</h3>
                  <p>
                    Estado: {getPayoutStatusLabel(payoutDetail.status)} · Semáforo: {getPayoutSemaforoLabel(payoutDetail)}
                  </p>
                </div>
                <button type="button" onClick={() => setPayoutDetail(null)}>Cerrar</button>
              </div>

              <div className="merchant-payout-detail-kpis">
                <div><span>Solicitado</span><strong>{formatMoney(payoutDetail.netAmountRequested || 0)}</strong></div>
                <div><span>Aprobado</span><strong>{formatMoney(payoutDetail.netAmountApproved || 0)}</strong></div>
                <div><span>Comisión</span><strong>{formatMoney(payoutDetail.commissionAmount || 0)}</strong></div>
                <div><span>Penalización</span><strong>{formatMoney(payoutDetail.penaltyAmount || 0)}</strong></div>
              </div>

              <div className="merchant-payout-detail-grid">
                <span>Solicitado</span><strong>{formatPayoutDateTime(payoutDetail.requestedAt)}</strong>
                <span>Recibido Admin</span><strong>{formatPayoutDateTime(payoutDetail.adminReceivedAt)}</strong>
                <span>Aprobado</span><strong>{formatPayoutDateTime(payoutDetail.approvedAt)}</strong>
                <span>Pagado</span><strong>{formatPayoutDateTime(payoutDetail.paidAt)}</strong>
                <span>Confirmado comercio</span><strong>{formatPayoutDateTime(payoutDetail.receivedConfirmedAt)}</strong>
                <span>Referencia</span><strong>{payoutDetail.transferReference || "—"}</strong>
                <span>Comprobante</span><PayoutProofAction row={payoutDetail} />
                <span>Responsable</span><strong>{payoutDetail.currentResponsible || "—"}</strong>
              </div>

              <div className="merchant-payout-timeline">
                <h4>Timeline / auditoría</h4>
                {(payoutDetail.auditEvents || []).map((event, index) => (
                  <div className="merchant-payout-timeline-item" key={`${event.eventType}-${index}`}>
                    <span>{index + 1}</span>
                    <div>
                      <strong>{getPayoutAuditLabel(event.eventType)}</strong>
                      <p>{event.comment || "Sin comentario"}</p>
                      <small>{formatPayoutDateTime(event.createdAt)}{event.actorName ? ` · ${event.actorName}` : ""}</small>
                    </div>
                  </div>
                ))}
                {!(payoutDetail.auditEvents || []).length ? (
                  <p className="merchant-payout-empty-timeline">Sin eventos de auditoría.</p>
                ) : null}
              </div>
            </section>
          </div>
        ) : null}

      {credentialPanel.open ? (
        <div className="merchant-credentials-overlay" role="presentation" onClick={closeCredentialPanel}>
          <section
            className="merchant-credentials-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Credenciales PWA del comercio"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="merchant-credentials-header">
              <div>
                <span>Acceso PWA Comercio</span>
                <h3>{credentialPanel.merchant?.name || "Comercio"}</h3>
                <p>
                  No se enviará correo. Este usuario y contraseña temporal sirven para ingresar a
                  comercios.superagrodig.com y luego copiarlos por WhatsApp.
                </p>
              </div>

              <button type="button" onClick={closeCredentialPanel} aria-label="Cerrar panel">
                ×
              </button>
            </div>

            <div className="merchant-credentials-status">
              <strong>
                {credentialPanel.merchant?.ownerUserId ? "Usuario ya asociado" : "Usuario pendiente"}
              </strong>
              <span>
                {credentialPanel.merchant?.ownerUserId
                  ? `Owner ID: ${credentialPanel.merchant.ownerUserId}`
                  : "Este comercio aún no tiene usuario PWA enlazado."}
              </span>
            </div>

            <div className="merchant-credentials-fields">
              <label>
                Nombre para acceso
                <input
                  type="text"
                  value={credentialPanel.name}
                  onChange={(event) => updateCredentialPanel("name", event.target.value)}
                />
              </label>

              <label>
                Usuario de acceso
                <input
                  type="email"
                  value={credentialPanel.email}
                  onChange={(event) => updateCredentialPanel("email", event.target.value)}
                />
              </label>
            </div>

            <div className="merchant-credentials-warning">
              No se enviará ningún correo. Al crear el acceso, copia el usuario y la contraseña temporal para enviarlos por WhatsApp.
            </div>

            {credentialPanel.loading ? (
              <div className="merchant-credentials-hint">
                Creando acceso PWA, espera unos segundos...
              </div>
            ) : null}

            {!credentialPanel.loading && !credentialPanel.temporaryPassword && !credentialPanel.error ? (
              <div className="merchant-credentials-hint">
                La contraseña temporal aparecerá aquí después de crear el acceso.
              </div>
            ) : null}

            {credentialPanel.error ? (
              <div className="merchant-credentials-error">
                <strong>No se pudo crear el acceso</strong>
                <span>{credentialPanel.error}</span>
              </div>
            ) : null}

            {credentialPanel.successMessage ? (
              <div className="merchant-credentials-success">
                {credentialPanel.successMessage}
              </div>
            ) : null}

            {credentialPanel.temporaryPassword ? (
              <div className="merchant-temporary-password">
                <span>Contraseña temporal</span>
                <strong>{credentialPanel.temporaryPassword}</strong>
                <button type="button" onClick={copyCredentialAccess}>
                  Copiar para WhatsApp
                </button>
              </div>
            ) : null}

            {credentialPanel.user ? (
              <div className="merchant-credentials-user">
                <span>Usuario creado</span>
                <strong>{credentialPanel.user.email}</strong>
                <small>{credentialPanel.user.role || "merchant_owner"}</small>
              </div>
            ) : null}

            <div className="merchant-credentials-actions">
              <button type="button" className="merchant-secondary-button" onClick={closeCredentialPanel}>
                Cerrar
              </button>
              <button
                type="button"
                className="merchant-primary-button"
                onClick={generateMerchantCredentials}
                disabled={credentialPanel.loading}
              >
                {credentialPanel.loading
                  ? "Generando..."
                  : credentialPanel.merchant?.ownerUserId
                    ? "Regenerar acceso PWA"
                    : "Crear acceso PWA"}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      <ToastContainer />
    </div>
  );
}

export default Merchants;
